import {
  TEntityResponse,
  TEntityListResponse,
  TCreateReportReqBody,
  TReportResBody,
  TReportSummaryResBody,
  TUpdateReportReqBody,
  TReportLogSummaryResBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TReportsClient = {
  create(body: TCreateReportReqBody): Promise<TEntityResponse<'report', TReportResBody> | undefined>;
  update(id: string, body: TUpdateReportReqBody): Promise<TEntityResponse<'report', TReportResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'report', TReportResBody> | undefined>;
  list(
    limit: number,
    page: number,
    sort?: string,
    filter?: string
  ): Promise<TEntityListResponse<'report', TReportSummaryResBody> | undefined>;
  list_report_logs(
    limit: number,
    page: number,
    reportId: string
  ): Promise<TEntityListResponse<'report-log', TReportLogSummaryResBody> | undefined>;
};

function ReportsClient(apiClient: TAPIClient): TReportsClient {
  return {
    async create(body: TCreateReportReqBody): Promise<TEntityResponse<'report', TReportResBody> | undefined> {
      const data = await apiClient.post<TCreateReportReqBody, TEntityResponse<'report', TReportResBody>>(
        '/reports',
        body
      );

      if (data && data.type !== 'report') {
        throw new Error('Invalid entity type received, expected: report, received: ' + data.type);
      }

      return data;
    },

    async update(
      id: string,
      body: TUpdateReportReqBody
    ): Promise<TEntityResponse<'report', TReportResBody> | undefined> {
      const data = await apiClient.put<TUpdateReportReqBody, TEntityResponse<'report', TReportResBody>>(
        `/reports/${id}`,
        body
      );

      if (data && data.type !== 'report') {
        throw new Error('Invalid entity type received, expected: report, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/reports/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'report', TReportResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'report', TReportResBody>>(`/reports/${id}`);

      if (data && data.type !== 'report') {
        throw new Error('Invalid entity type received, expected: report, received: ' + data.type);
      }

      return data;
    },

    async list(
      limit: number,
      page: number,
      sort?: string,
      filter?: string
    ): Promise<TEntityListResponse<'report', TReportSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'report', TReportSummaryResBody>>(
        `/reports?limit=${limit}&page=${page}${sort}&${filter}`
      );

      if (data && data.type !== 'report') {
        throw new Error('Invalid entity type received, expected: report, received: ' + data.type);
      }

      return data;
    },

    async list_report_logs(
      limit: number,
      page: number,
      reportId: string
    ): Promise<TEntityListResponse<'report-log', TReportLogSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'report-log', TReportLogSummaryResBody>>(
        `/report-logs?limit=${limit}&page=${page}&filter[report_id]=${reportId}&sort[created_at]=desc`
      );

      if (data && data.type !== 'report-log') {
        throw new Error('Invalid entity type received, expected: report-log, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TReportsClient, ReportsClient };
